import { useEffect, useState, useCallback } from 'react'
import { useAuth } from '../../context/AuthContext'
import { supabase } from '../../supabaseClient'
import {
  Plus, X, TrendingUp, TrendingDown, Wallet, Target,
  Edit2, Check, Flag, CheckCircle2, CreditCard, Calendar,
} from 'lucide-react'

const fmt = (n) =>
  new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n || 0)

const fmtDate = (d) =>
  new Date(d + 'T00:00:00').toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })

const CATEGORIE_ENTRATA = ['Acconto', 'Saldo cliente', 'Rimborso spese', 'Altro']
const CATEGORIE_USCITA  = ['Attrezzatura', 'Personale / Cast', 'Post-produzione', 'Trasferta / Logistica', 'Location', 'Licenze / Software', 'Catering', 'Marketing', 'Altro']

const TIPO_COLOR = {
  entrata: { bg: 'bg-green-500/10', text: 'text-green-400', border: 'border-green-500/20', dot: 'bg-green-400' },
  uscita:  { bg: 'bg-red-500/10',   text: 'text-red-400',   border: 'border-red-500/20',   dot: 'bg-red-400'   },
}

const MILESTONE_STATUS = {
  attesa:    { label: 'In attesa',  bg: 'bg-yellow-500/10', text: 'text-yellow-400', border: 'border-yellow-500/20', icon: Flag },
  raggiunta: { label: 'Raggiunta', bg: 'bg-blue-500/10',   text: 'text-blue-400',   border: 'border-blue-500/20',   icon: CheckCircle2 },
  pagata:    { label: 'Pagata',    bg: 'bg-green-500/10',  text: 'text-green-400',  border: 'border-green-500/20',  icon: CreditCard },
}

// ─── VoceModal ─────────────────────────────────────────────────────────────────
const VoceModal = ({ voce, projectId, onClose, onSaved, onDeleted }) => {
  const { profile } = useAuth()
  const isEdit = !!voce?.id

  const [tipo, setTipo] = useState(voce?.tipo || 'uscita')
  const [categoria, setCategoria] = useState(voce?.categoria || '')
  const [descrizione, setDescrizione] = useState(voce?.descrizione || '')
  const [importo, setImporto] = useState(voce?.importo?.toString() || '')
  const [data, setData] = useState(voce?.data || new Date().toISOString().split('T')[0])
  const [note, setNote] = useState(voce?.note || '')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const categorie = tipo === 'entrata' ? CATEGORIE_ENTRATA : CATEGORIE_USCITA

  useEffect(() => {
    if (!voce?.id) setCategoria('')
  }, [tipo, voce?.id])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!descrizione.trim() || !importo || !categoria) return
    setLoading(true)
    setError(null)

    const payload = {
      tipo,
      categoria,
      descrizione: descrizione.trim(),
      importo: parseFloat(importo),
      data,
      note: note.trim() || null,
      project_id: projectId || null,
    }

    let data2, err
    if (isEdit) {
      ;({ data: data2, error: err } = await supabase.from('finance_voci').update(payload).eq('id', voce.id).select().single())
    } else {
      ;({ data: data2, error: err } = await supabase.from('finance_voci').insert({ ...payload, created_by: profile.id }).select().single())
    }

    if (err) { setError(err.message); setLoading(false); return }
    onSaved(data2)
    onClose()
  }

  const handleDelete = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return }
    await supabase.from('finance_voci').delete().eq('id', voce.id)
    onDeleted(voce.id)
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4 py-6">
      <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gray-900 border-b border-gray-800 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <h2 className="text-lg font-bold text-white">{isEdit ? 'Modifica voce' : 'Nuova voce'}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors"><X size={20} /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            {['uscita', 'entrata'].map(t => (
              <button
                key={t}
                type="button"
                onClick={() => setTipo(t)}
                className={`py-3 rounded-xl text-sm font-semibold border transition-colors capitalize ${
                  tipo === t
                    ? t === 'entrata'
                      ? 'bg-green-500/20 border-green-500/40 text-green-400'
                      : 'bg-red-500/20 border-red-500/40 text-red-400'
                    : 'bg-gray-800 border-gray-700 text-gray-500 hover:border-gray-600'
                }`}
              >
                {t === 'entrata' ? '↑ Entrata' : '↓ Uscita'}
              </button>
            ))}
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Categoria *</label>
            <select
              value={categoria}
              onChange={e => setCategoria(e.target.value)}
              required
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-violet-500 transition-colors"
            >
              <option value="">Seleziona categoria</option>
              {categorie.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Descrizione *</label>
            <input
              value={descrizione}
              onChange={e => setDescrizione(e.target.value)}
              required
              autoFocus
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-violet-500 transition-colors"
              placeholder="Es. Noleggio camera RED..."
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Importo (€) *</label>
              <input
                type="number"
                value={importo}
                onChange={e => setImporto(e.target.value)}
                required
                min="0.01"
                step="0.01"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-violet-500 transition-colors"
                placeholder="0,00"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Data *</label>
              <input
                type="date"
                value={data}
                onChange={e => setData(e.target.value)}
                required
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-violet-500 transition-colors"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Note</label>
            <textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              rows={2}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-violet-500 transition-colors resize-none"
              placeholder="Note aggiuntive..."
            />
          </div>

          {error && <p className="text-red-400 text-sm">{error}</p>}

          <div className="flex gap-3 pt-2">
            {isEdit && (
              <button
                type="button"
                onClick={handleDelete}
                className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  confirmDelete
                    ? 'bg-red-500/20 text-red-400 border border-red-500/40 hover:bg-red-500 hover:text-white'
                    : 'border border-gray-700 text-gray-500 hover:text-red-400 hover:border-red-500/40'
                }`}
              >
                {confirmDelete ? 'Conferma eliminazione' : 'Elimina'}
              </button>
            )}
            <button type="button" onClick={onClose}
              className="flex-1 py-3 rounded-lg border border-gray-700 text-gray-400 hover:text-white transition-colors text-sm">
              Annulla
            </button>
            <button
              type="submit"
              disabled={loading || !descrizione.trim() || !importo || !categoria}
              className="flex-1 py-3 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white font-medium text-sm transition-colors"
            >
              {loading ? 'Salvataggio...' : isEdit ? 'Salva' : 'Aggiungi'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── BudgetInput (inline edit) ─────────────────────────────────────────────────
const BudgetInput = ({ value, onSave }) => {
  const [editing, setEditing] = useState(false)
  const [val, setVal] = useState(value?.toString() || '')

  const handleSave = () => {
    const n = parseFloat(val)
    onSave(isNaN(n) || n <= 0 ? null : n)
    setEditing(false)
  }

  if (editing) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-gray-500 text-sm">€</span>
        <input
          type="number"
          value={val}
          onChange={e => setVal(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setEditing(false) }}
          autoFocus
          min="0"
          step="0.01"
          className="w-36 bg-gray-800 border border-violet-500 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none"
          placeholder="0,00"
        />
        <button onClick={handleSave} className="text-green-400 hover:text-green-300 transition-colors"><Check size={16} /></button>
        <button onClick={() => setEditing(false)} className="text-gray-500 hover:text-white transition-colors"><X size={16} /></button>
      </div>
    )
  }

  return (
    <button
      onClick={() => { setVal(value?.toString() || ''); setEditing(true) }}
      className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors group"
    >
      <span className="text-white font-bold text-xl">{value ? fmt(value) : '—'}</span>
      <Edit2 size={13} className="opacity-0 group-hover:opacity-100 transition-opacity" />
    </button>
  )
}

// ─── MilestoneModal ────────────────────────────────────────────────────────────
const MilestoneModal = ({ milestone, projectId, onClose, onSaved, onDeleted }) => {
  const { profile } = useAuth()
  const isEdit = !!milestone?.id

  const [titolo, setTitolo] = useState(milestone?.titolo || '')
  const [descrizione, setDescrizione] = useState(milestone?.descrizione || '')
  const [dataTarget, setDataTarget] = useState(milestone?.data_target || '')
  const [importo, setImporto] = useState(milestone?.importo?.toString() || '0')
  const [status, setStatus] = useState(milestone?.status || 'attesa')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!titolo.trim()) return
    setLoading(true)
    setError(null)

    const payload = {
      titolo: titolo.trim(),
      descrizione: descrizione.trim() || null,
      data_target: dataTarget || null,
      importo: parseFloat(importo) || 0,
      status,
      project_id: projectId,
    }

    let data, err
    if (isEdit) {
      ;({ data, error: err } = await supabase.from('milestones').update(payload).eq('id', milestone.id).select().single())

      // notifica se status cambiato a raggiunta o pagata
      if (!err && data && data.status !== milestone.status && (data.status === 'raggiunta' || data.status === 'pagata')) {
        const { data: members } = await supabase
          .from('project_members')
          .select('user_id')
          .eq('project_id', projectId)

        if (members?.length) {
          const tipo = data.status === 'raggiunta' ? 'milestone_raggiunta' : 'pagamento_ricevuto'
          const titoloNotif = data.status === 'raggiunta' ? '🏁 Milestone raggiunta' : '💰 Pagamento ricevuto'
          const body = `"${data.titolo}"${data.importo > 0 ? ` — ${fmt(data.importo)}` : ''}`

          await supabase.from('notifiche').insert(
            members
              .filter(m => m.user_id !== profile.id)
              .map(m => ({
                user_id: m.user_id,
                tipo,
                titolo: titoloNotif,
                body,
                link: '/finance',
              }))
          )
        }
      }
    } else {
      ;({ data, error: err } = await supabase.from('milestones').insert({ ...payload, created_by: profile.id }).select().single())
    }

    if (err) { setError(err.message); setLoading(false); return }
    onSaved(data, isEdit)
    onClose()
  }

  const handleDelete = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return }
    await supabase.from('milestones').delete().eq('id', milestone.id)
    onDeleted(milestone.id)
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4 py-6">
      <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gray-900 border-b border-gray-800 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <h2 className="text-lg font-bold text-white">{isEdit ? 'Modifica milestone' : 'Nuova milestone'}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors"><X size={20} /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Titolo */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">Titolo *</label>
            <input
              value={titolo}
              onChange={e => setTitolo(e.target.value)}
              required
              autoFocus
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-violet-500 transition-colors"
              placeholder="Es. Consegna bozze, Saldo finale..."
            />
          </div>

          {/* Descrizione */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">Note</label>
            <textarea
              value={descrizione}
              onChange={e => setDescrizione(e.target.value)}
              rows={2}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-violet-500 transition-colors resize-none"
              placeholder="Dettagli aggiuntivi..."
            />
          </div>

          {/* Data + Importo */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Data target</label>
              <input
                type="date"
                value={dataTarget}
                onChange={e => setDataTarget(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-violet-500 transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Importo (€)</label>
              <input
                type="number"
                value={importo}
                onChange={e => setImporto(e.target.value)}
                min="0"
                step="0.01"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-violet-500 transition-colors"
                placeholder="0,00"
              />
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Status</label>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(MILESTONE_STATUS).map(([key, cfg]) => {
                const Icon = cfg.icon
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setStatus(key)}
                    className={`flex flex-col items-center gap-1.5 py-3 rounded-xl text-xs font-semibold border transition-colors ${
                      status === key
                        ? `${cfg.bg} ${cfg.border} ${cfg.text}`
                        : 'bg-gray-800 border-gray-700 text-gray-500 hover:border-gray-600'
                    }`}
                  >
                    <Icon size={16} />
                    {cfg.label}
                  </button>
                )
              })}
            </div>
          </div>

          {error && <p className="text-red-400 text-sm">{error}</p>}

          <div className="flex gap-3 pt-2">
            {isEdit && (
              <button
                type="button"
                onClick={handleDelete}
                className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  confirmDelete
                    ? 'bg-red-500/20 text-red-400 border border-red-500/40 hover:bg-red-500 hover:text-white'
                    : 'border border-gray-700 text-gray-500 hover:text-red-400 hover:border-red-500/40'
                }`}
              >
                {confirmDelete ? 'Conferma eliminazione' : 'Elimina'}
              </button>
            )}
            <button type="button" onClick={onClose}
              className="flex-1 py-3 rounded-lg border border-gray-700 text-gray-400 hover:text-white transition-colors text-sm">
              Annulla
            </button>
            <button
              type="submit"
              disabled={loading || !titolo.trim()}
              className="flex-1 py-3 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white font-medium text-sm transition-colors"
            >
              {loading ? 'Salvataggio...' : isEdit ? 'Salva' : 'Crea'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Finance ───────────────────────────────────────────────────────────────────
const Finance = () => {
  const { profile } = useAuth()
  const [progetti, setProgetti] = useState([])
  const [selectedId, setSelectedId] = useState('')
  const [progetto, setProgetto] = useState(null)
  const [activeTab, setActiveTab] = useState('voci') // 'voci' | 'milestones'

  // Voci
  const [voci, setVoci] = useState([])
  const [loadingVoci, setLoadingVoci] = useState(false)
  const [showVoceModal, setShowVoceModal] = useState(false)
  const [modalVoce, setModalVoce] = useState(null)
  const [filtroTipo, setFiltroTipo] = useState('tutti')

  // Milestones
  const [milestones, setMilestones] = useState([])
  const [loadingMilestones, setLoadingMilestones] = useState(false)
  const [showMilestoneModal, setShowMilestoneModal] = useState(false)
  const [modalMilestone, setModalMilestone] = useState(null)

  const [loading, setLoading] = useState(true)
  const isAdmin = profile?.role === 'admin'

  useEffect(() => {
    supabase.from('progetti').select('id, titolo, colore, budget, status').order('created_at', { ascending: false })
      .then(({ data }) => { setProgetti(data || []); setLoading(false) })
  }, [])

  const fetchVoci = useCallback(async (pid) => {
    if (!pid) return
    setLoadingVoci(true)
    const { data } = await supabase
      .from('finance_voci')
      .select('*')
      .eq('project_id', pid)
      .order('data', { ascending: false })
    setVoci(data || [])
    setLoadingVoci(false)
  }, [])

  const fetchMilestones = useCallback(async (pid) => {
    if (!pid) return
    setLoadingMilestones(true)
    const { data } = await supabase
      .from('milestones')
      .select('*')
      .eq('project_id', pid)
      .order('data_target', { ascending: true, nullsFirst: false })
    setMilestones(data || [])
    setLoadingMilestones(false)
  }, [])

  useEffect(() => {
    if (!selectedId) return
    const p = progetti.find(p => p.id === selectedId)
    setProgetto(p || null)
    setVoci([])
    setMilestones([])
    fetchVoci(selectedId)
    fetchMilestones(selectedId)

    const channel = supabase
      .channel(`finance-${selectedId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'finance_voci', filter: `project_id=eq.${selectedId}` }, () => fetchVoci(selectedId))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'milestones', filter: `project_id=eq.${selectedId}` }, () => fetchMilestones(selectedId))
      .subscribe()
    return () => supabase.removeChannel(channel)
  }, [selectedId, progetti, fetchVoci, fetchMilestones])

  const handleSaveBudget = async (budget) => {
    await supabase.from('progetti').update({ budget }).eq('id', selectedId)
    setProgetti(prev => prev.map(p => p.id === selectedId ? { ...p, budget } : p))
    setProgetto(prev => ({ ...prev, budget }))
  }

  const handleVoceSaved = (saved) => {
    setVoci(prev => {
      const exists = prev.find(v => v.id === saved.id)
      return exists ? prev.map(v => v.id === saved.id ? saved : v) : [saved, ...prev]
    })
  }
  const handleVoceDeleted = (id) => setVoci(prev => prev.filter(v => v.id !== id))

  const handleMilestoneSaved = (saved, isEdit) => {
    setMilestones(prev => {
      if (isEdit) return prev.map(m => m.id === saved.id ? saved : m)
      return [...prev, saved].sort((a, b) => {
        if (!a.data_target) return 1
        if (!b.data_target) return -1
        return new Date(a.data_target) - new Date(b.data_target)
      })
    })
  }
  const handleMilestoneDeleted = (id) => setMilestones(prev => prev.filter(m => m.id !== id))

  // Quick status change
  const cycleStatus = async (m) => {
    const order = ['attesa', 'raggiunta', 'pagata']
    const next = order[(order.indexOf(m.status) + 1) % order.length]
    const { data } = await supabase.from('milestones').update({ status: next }).eq('id', m.id).select().single()
    if (data) {
      handleMilestoneSaved(data, true)

      // notifica membri
      if ((next === 'raggiunta' || next === 'pagata')) {
        const { data: members } = await supabase
          .from('project_members')
          .select('user_id')
          .eq('project_id', selectedId)

        if (members?.length) {
          const tipo = next === 'raggiunta' ? 'milestone_raggiunta' : 'pagamento_ricevuto'
          const titoloNotif = next === 'raggiunta' ? '🏁 Milestone raggiunta' : '💰 Pagamento ricevuto'
          const body = `"${data.titolo}"${data.importo > 0 ? ` — ${fmt(data.importo)}` : ''}`
          await supabase.from('notifiche').insert(
            members
              .filter(mem => mem.user_id !== profile?.id)
              .map(mem => ({
                user_id: mem.user_id,
                tipo,
                titolo: titoloNotif,
                body,
                link: '/finance',
              }))
          )
        }
      }
    }
  }

  // Calcoli voci
  const totEntrate = voci.filter(v => v.tipo === 'entrata').reduce((s, v) => s + Number(v.importo), 0)
  const totUscite  = voci.filter(v => v.tipo === 'uscita').reduce((s, v) => s + Number(v.importo), 0)
  const saldo      = totEntrate - totUscite
  const budget     = progetto?.budget || 0
  const percUscite  = budget > 0 ? Math.min((totUscite / budget) * 100, 100) : 0
  const percEntrate = budget > 0 ? Math.min((totEntrate / budget) * 100, 100) : 0

  // Calcoli milestones
  const totMilestone = milestones.reduce((s, m) => s + Number(m.importo || 0), 0)
  const pagate = milestones.filter(m => m.status === 'pagata').reduce((s, m) => s + Number(m.importo || 0), 0)

  const vociFiltrate = filtroTipo === 'tutti' ? voci : voci.filter(v => v.tipo === filtroTipo)

  const perCategoria = {}
  vociFiltrate.forEach(v => {
    const key = `${v.tipo}::${v.categoria}`
    if (!perCategoria[key]) perCategoria[key] = { tipo: v.tipo, categoria: v.categoria, voci: [], tot: 0 }
    perCategoria[key].voci.push(v)
    perCategoria[key].tot += Number(v.importo)
  })
  const gruppi = Object.values(perCategoria).sort((a, b) => {
    if (a.tipo !== b.tipo) return a.tipo === 'uscita' ? -1 : 1
    return b.tot - a.tot
  })

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
    </div>
  )

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Finance</h1>
          <p className="text-gray-500 text-sm mt-1">Budget, spese e milestones per progetto</p>
        </div>
        {selectedId && activeTab === 'voci' && (
          <button
            onClick={() => { setModalVoce({}); setShowVoceModal(true) }}
            className="flex items-center gap-2 bg-violet-600 hover:bg-violet-500 text-white font-medium px-4 py-2.5 rounded-lg transition-colors text-sm"
          >
            <Plus size={16} />
            Nuova voce
          </button>
        )}
        {selectedId && activeTab === 'milestones' && (
          <button
            onClick={() => { setModalMilestone(null); setShowMilestoneModal(true) }}
            className="flex items-center gap-2 bg-violet-600 hover:bg-violet-500 text-white font-medium px-4 py-2.5 rounded-lg transition-colors text-sm"
          >
            <Plus size={16} />
            Nuova milestone
          </button>
        )}
      </div>

      {/* Selector progetto */}
      <div className="mb-6">
        <select
          value={selectedId}
          onChange={e => { setSelectedId(e.target.value); setActiveTab('voci') }}
          className="w-full md:w-80 bg-gray-900 border border-gray-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-violet-500 transition-colors"
        >
          <option value="">Seleziona un progetto...</option>
          {progetti.map(p => (
            <option key={p.id} value={p.id}>{p.titolo}</option>
          ))}
        </select>
      </div>

      {!selectedId && (
        <div className="text-center py-24">
          <Wallet size={48} className="text-gray-700 mx-auto mb-4" />
          <p className="text-gray-600">Seleziona un progetto per vedere il suo budget</p>
        </div>
      )}

      {selectedId && (
        <div className="space-y-6">

          {/* Stat cards */}
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
            <div className="bg-gray-900 border border-gray-800 rounded-2xl px-5 py-4">
              <div className="flex items-center gap-2 mb-2">
                <Target size={16} className="text-violet-400" />
                <span className="text-xs text-gray-500 uppercase tracking-wider">Budget</span>
                {isAdmin && <span className="text-xs text-gray-600 ml-auto">click per modificare</span>}
              </div>
              {isAdmin
                ? <BudgetInput value={budget || null} onSave={handleSaveBudget} />
                : <p className="text-xl font-bold text-white">{budget ? fmt(budget) : '—'}</p>
              }
            </div>

            <div className="bg-green-500/10 border border-green-500/20 rounded-2xl px-5 py-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp size={16} className="text-green-400" />
                <span className="text-xs text-gray-500 uppercase tracking-wider">Entrate</span>
              </div>
              <p className="text-xl font-bold text-green-400">{fmt(totEntrate)}</p>
            </div>

            <div className="bg-red-500/10 border border-red-500/20 rounded-2xl px-5 py-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingDown size={16} className="text-red-400" />
                <span className="text-xs text-gray-500 uppercase tracking-wider">Uscite</span>
              </div>
              <p className="text-xl font-bold text-red-400">{fmt(totUscite)}</p>
            </div>

            <div className={`border rounded-2xl px-5 py-4 ${saldo >= 0 ? 'bg-blue-500/10 border-blue-500/20' : 'bg-orange-500/10 border-orange-500/20'}`}>
              <div className="flex items-center gap-2 mb-2">
                <Wallet size={16} className={saldo >= 0 ? 'text-blue-400' : 'text-orange-400'} />
                <span className="text-xs text-gray-500 uppercase tracking-wider">Saldo</span>
              </div>
              <p className={`text-xl font-bold ${saldo >= 0 ? 'text-blue-400' : 'text-orange-400'}`}>{fmt(saldo)}</p>
            </div>
          </div>

          {/* Progress bars vs budget */}
          {budget > 0 && (
            <div className="bg-gray-900 border border-gray-800 rounded-2xl px-5 py-4 space-y-3">
              <div>
                <div className="flex justify-between text-xs text-gray-500 mb-1.5">
                  <span>Uscite vs budget</span>
                  <span>{percUscite.toFixed(0)}%</span>
                </div>
                <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${percUscite >= 90 ? 'bg-red-400' : percUscite >= 70 ? 'bg-orange-400' : 'bg-violet-500'}`}
                    style={{ width: `${percUscite}%` }}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-xs text-gray-500 mb-1.5">
                  <span>Entrate vs budget</span>
                  <span>{percEntrate.toFixed(0)}%</span>
                </div>
                <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-green-500 transition-all" style={{ width: `${percEntrate}%` }} />
                </div>
              </div>
            </div>
          )}

          {/* Tab switcher */}
          <div className="flex gap-1 bg-gray-900 border border-gray-800 rounded-xl p-1 w-fit">
            {[
              { key: 'voci', label: 'Voci finanziarie' },
              { key: 'milestones', label: `Milestones${milestones.length > 0 ? ` (${milestones.length})` : ''}` },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === tab.key
                    ? 'bg-violet-600 text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* ── Tab: Voci ── */}
          {activeTab === 'voci' && (
            <>
              {voci.length > 0 && (
                <div className="flex gap-2">
                  {['tutti', 'uscita', 'entrata'].map(t => (
                    <button
                      key={t}
                      onClick={() => setFiltroTipo(t)}
                      className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors capitalize ${
                        filtroTipo === t
                          ? 'bg-violet-600 text-white'
                          : 'bg-gray-800 text-gray-400 hover:text-white'
                      }`}
                    >
                      {t === 'tutti' ? 'Tutti' : t === 'entrata' ? 'Entrate' : 'Uscite'}
                    </button>
                  ))}
                </div>
              )}

              {loadingVoci ? (
                <div className="flex justify-center py-12">
                  <div className="w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : voci.length === 0 ? (
                <div className="text-center py-16">
                  <p className="text-gray-600 mb-4">Nessuna voce registrata per questo progetto.</p>
                  <button
                    onClick={() => { setModalVoce({}); setShowVoceModal(true) }}
                    className="text-violet-400 hover:text-violet-300 text-sm transition-colors"
                  >
                    Aggiungi la prima voce →
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {gruppi.map(({ tipo, categoria, voci: gVoci, tot }) => {
                    const c = TIPO_COLOR[tipo]
                    return (
                      <div key={`${tipo}-${categoria}`} className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
                        <div className={`flex items-center justify-between px-4 py-3 border-b border-gray-800 ${c.bg}`}>
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${c.dot}`} />
                            <span className="text-sm font-semibold text-white">{categoria}</span>
                            <span className="text-xs text-gray-600 bg-gray-800/60 px-2 py-0.5 rounded-full">{gVoci.length}</span>
                          </div>
                          <span className={`text-sm font-bold ${c.text}`}>
                            {tipo === 'uscita' ? '−' : '+'}{fmt(tot)}
                          </span>
                        </div>
                        <div className="divide-y divide-gray-800">
                          {gVoci.map(v => (
                            <div
                              key={v.id}
                              onClick={() => { setModalVoce(v); setShowVoceModal(true) }}
                              className="flex items-center gap-4 px-4 py-3 hover:bg-gray-800/40 transition-colors cursor-pointer"
                            >
                              <div className="flex-1 min-w-0">
                                <p className="text-white text-sm font-medium truncate">{v.descrizione}</p>
                                {v.note && <p className="text-gray-600 text-xs truncate mt-0.5">{v.note}</p>}
                              </div>
                              <span className="text-xs text-gray-500 flex-shrink-0">{fmtDate(v.data)}</span>
                              <span className={`text-sm font-semibold flex-shrink-0 ${c.text}`}>
                                {v.tipo === 'uscita' ? '−' : '+'}{fmt(v.importo)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </>
          )}

          {/* ── Tab: Milestones ── */}
          {activeTab === 'milestones' && (
            <>
              {/* Riepilogo milestones */}
              {milestones.length > 0 && (
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-gray-900 border border-gray-800 rounded-xl px-4 py-3">
                    <p className="text-xs text-gray-500 mb-1">Totale milestone</p>
                    <p className="text-lg font-bold text-white">{fmt(totMilestone)}</p>
                  </div>
                  <div className="bg-green-500/10 border border-green-500/20 rounded-xl px-4 py-3">
                    <p className="text-xs text-gray-500 mb-1">Incassato</p>
                    <p className="text-lg font-bold text-green-400">{fmt(pagate)}</p>
                  </div>
                  <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl px-4 py-3">
                    <p className="text-xs text-gray-500 mb-1">Da incassare</p>
                    <p className="text-lg font-bold text-yellow-400">{fmt(totMilestone - pagate)}</p>
                  </div>
                </div>
              )}

              {loadingMilestones ? (
                <div className="flex justify-center py-12">
                  <div className="w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : milestones.length === 0 ? (
                <div className="text-center py-16">
                  <Flag size={40} className="text-gray-700 mx-auto mb-3" />
                  <p className="text-gray-600 mb-4">Nessuna milestone per questo progetto.</p>
                  <button
                    onClick={() => { setModalMilestone(null); setShowMilestoneModal(true) }}
                    className="text-violet-400 hover:text-violet-300 text-sm transition-colors"
                  >
                    Crea la prima milestone →
                  </button>
                </div>
              ) : (
                <div className="relative">
                  {/* Timeline line */}
                  <div className="absolute left-6 top-4 bottom-4 w-px bg-gray-800" />

                  <div className="space-y-3">
                    {milestones.map((m) => {
                      const cfg = MILESTONE_STATUS[m.status] || MILESTONE_STATUS.attesa
                      const Icon = cfg.icon
                      const isScaduta = m.status === 'attesa' && m.data_target && new Date(m.data_target) < new Date()

                      return (
                        <div key={m.id} className="relative flex gap-4">
                          {/* Dot on timeline */}
                          <div className={`relative z-10 flex-shrink-0 w-12 h-12 rounded-full border-2 flex items-center justify-center ${
                            isScaduta
                              ? 'bg-red-500/10 border-red-500/40'
                              : `${cfg.bg} ${cfg.border}`
                          }`}>
                            <Icon size={18} className={isScaduta ? 'text-red-400' : cfg.text} />
                          </div>

                          {/* Card */}
                          <div
                            className="flex-1 bg-gray-900 border border-gray-800 rounded-2xl px-4 py-3 cursor-pointer hover:border-gray-700 transition-colors"
                            onClick={() => { setModalMilestone(m); setShowMilestoneModal(true) }}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <p className={`font-semibold text-sm ${isScaduta ? 'text-red-300' : 'text-white'}`}>
                                  {m.titolo}
                                  {isScaduta && <span className="ml-2 text-xs text-red-400 font-normal">⚠ Scaduta</span>}
                                </p>
                                {m.descrizione && (
                                  <p className="text-xs text-gray-500 mt-0.5 truncate">{m.descrizione}</p>
                                )}
                                <div className="flex items-center gap-3 mt-2">
                                  {m.data_target && (
                                    <span className="flex items-center gap-1 text-xs text-gray-500">
                                      <Calendar size={11} />
                                      {fmtDate(m.data_target)}
                                    </span>
                                  )}
                                  {Number(m.importo) > 0 && (
                                    <span className={`text-xs font-semibold ${cfg.text}`}>
                                      {fmt(m.importo)}
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* Quick status toggle */}
                              <button
                                onClick={e => { e.stopPropagation(); cycleStatus(m) }}
                                title="Cambia status"
                                className={`flex-shrink-0 px-2.5 py-1 rounded-lg text-xs font-semibold border transition-colors hover:opacity-80 ${cfg.bg} ${cfg.border} ${cfg.text}`}
                              >
                                {cfg.label}
                              </button>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </>
          )}

        </div>
      )}

      {/* Modali */}
      {showVoceModal && (
        <VoceModal
          voce={modalVoce}
          projectId={selectedId}
          onClose={() => { setShowVoceModal(false); setModalVoce(null) }}
          onSaved={handleVoceSaved}
          onDeleted={handleVoceDeleted}
        />
      )}

      {showMilestoneModal && (
        <MilestoneModal
          milestone={modalMilestone}
          projectId={selectedId}
          onClose={() => { setShowMilestoneModal(false); setModalMilestone(null) }}
          onSaved={handleMilestoneSaved}
          onDeleted={handleMilestoneDeleted}
        />
      )}
    </div>
  )
}

export default Finance
